import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../../../config/api';

const AdminCampingStats = () => {
    const [stats, setStats] = useState({
        total_locations: 0,
        total_sites: 0,
        total_bookings: 0,
        total_revenue: 0,
        pending_bookings: 0,
        available_sites: 0,
        booked_sites: 0,
        popular_locations: [],
        recent_bookings: []
    });
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState('month');

    const fetchStats = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(buildApiUrl('/camping/admin/bookings/stats'), {
                params: { range: dateRange },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-auth-token': token
                }
            });

            const statsData = response.data.data || {};
            setStats({
                total_locations: Number(statsData.totalLocations || 0),
                total_sites: Number(statsData.totalSites || 0),
                total_bookings: Number(statsData.total || 0),
                total_revenue: Number(statsData.totalRevenue || 0),
                pending_bookings: Number(statsData.pending || 0),
                available_sites: Number(statsData.availableSites || 0),
                booked_sites: Number(statsData.bookedSites || 0),
                popular_locations: statsData.popular_locations || [],
                recent_bookings: statsData.recent || []
            });
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    if (loading) {
        return <div className="loading-spinner">Loading dashboard...</div>;
    }

    return (
        <div className="admin-stats">
            <div className="stats-header">
                <h2>Camping Dashboard</h2>
                <div className="date-range">
                    <button 
                        className={dateRange === 'week' ? 'active' : ''}
                        onClick={() => setDateRange('week')}
                    >
                        Week
                    </button>
                    <button 
                        className={dateRange === 'month' ? 'active' : ''}
                        onClick={() => setDateRange('month')}
                    >
                        Month
                    </button>
                    <button 
                        className={dateRange === 'year' ? 'active' : ''}
                        onClick={() => setDateRange('year')}
                    >
                        Year
                    </button>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#e3f2fd', color: '#1976d2' }}>
                        <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <div className="stat-content">
                        <h3>Total Locations</h3>
                        <p className="stat-number">{stats.total_locations}</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#e8f5e8', color: '#2e7d32' }}>
                        <i className="fas fa-campground"></i>
                    </div>
                    <div className="stat-content">
                        <h3>Total Sites</h3>
                        <p className="stat-number">{stats.total_sites}</p>
                        <div className="stat-sub">
                            <span className="available">{stats.available_sites} Available</span>
                            <span className="booked">{stats.booked_sites} Booked</span>
                        </div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#fff3e0', color: '#ed6c02' }}>
                        <i className="fas fa-calendar-check"></i>
                    </div>
                    <div className="stat-content">
                        <h3>Total Bookings</h3>
                        <p className="stat-number">{stats.total_bookings}</p>
                        <div className="stat-sub">
                            <span className="pending">{stats.pending_bookings} Pending</span>
                        </div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#f3e5f5', color: '#7b1fa2' }}>
                        <i className="fas fa-rupee-sign"></i>
                    </div>
                    <div className="stat-content">
                        <h3>Total Revenue</h3>
                        <p className="stat-number">{formatCurrency(stats.total_revenue)}</p>
                    </div>
                </div>
            </div>

            <div className="stats-charts">
                <div className="chart-card">
                    <h3>Popular Locations</h3>
                    <div className="popular-locations">
                        {stats.popular_locations?.map(loc => (
                            <div key={loc.id} className="location-item">
                                <span className="location-name">{loc.name}</span>
                                <div className="location-stats">
                                    <span className="sites">{loc.sites_count} sites</span>
                                    <span className="bookings">{loc.bookings_count} bookings</span>
                                </div>
                                <div className="progress-bar">
                                    <div 
                                        className="progress" 
                                        style={{ width: `${(loc.bookings_count / stats.total_bookings) * 100}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="chart-card">
                    <h3>Recent Bookings</h3>
                    <div className="recent-bookings">
                        {stats.recent_bookings?.map(booking => (
                            <div key={booking.id} className="booking-item">
                                <div className="booking-info">
                                    <strong>{booking.customer_name}</strong>
                                    <span>{booking.site_name}</span>
                                </div>
                                <div className="booking-meta">
                                    <span className={`badge ${booking.booking_status}`}>
                                        {booking.booking_status}
                                    </span>
                                    <span className="amount">₹{booking.total_amount}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminCampingStats;
